import { MainSection } from "@/styles/global";
import React from "react";
import S from "./FormBookLocation.styled";
import axios from "axios";
import FormSuccess from "@/components/global-components/FormSuccess";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import sv from "date-fns/locale/sv";
import { Controller, useForm } from "react-hook-form";

export interface FormBookLocationProps {}

type FormData = {
  dovas_request_category: string;
  bokningFor: string;
  namn: string;
  personNummer?: string;
  email: string;
  adress: string;
  postNummer: string;
  stad: string;
  lokal: string;
  valAvSyfte: string;
  startDatum: string;
  slutDatum: string;
  syfteBeskrivning: string;
  ovrigt?: string;
  medlem?: string;
  organisationTyp?: string;
};

type FormState = {
  isLoading: boolean;
  error?: string;
  data: FormData;
  success: boolean;
};

const emptyForm = (): FormData => ({
  dovas_request_category: "Boka lokal",
  bokningFor: "Privatperson",
  namn: "",
  email: "",
  adress: "",
  postNummer: "",
  stad: "",
  lokal: "",
  valAvSyfte: "",
  startDatum: "",
  slutDatum: "",
  syfteBeskrivning: "",
});

const emptyFormState = (): FormState => ({
  isLoading: false,
  data: emptyForm(),
  success: false,
});

const FormBookLocation: React.FC<FormBookLocationProps> = () => {
  const [active, setActive] = React.useState("Privatperson");
  const [formState, setFormState] = React.useState(emptyFormState());

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  // update form data
  const updateFormProperty = <K extends keyof FormData>(
    property: K,
    value: FormData[K]
  ): void => {
    setFormState((state: FormState) => ({
      ...state,
      data: {
        ...state.data,
        [property]: value,
      },
    }));
  };

  const submitForm = handleSubmit(() => {
    setFormState((state) => ({
      ...state,
      isLoading: true,
    }));
    axios
      .post(process.env.GATSBY_CONTACT_URL || "", {
        ...formState.data,
      })
      .then((res) => {
        if (!res.data.success) {
          setFormState((state) => ({
            ...state,
            isLoading: false,
            error: res.data.error_msg,
          }));
        } else {
          setFormState((state) => ({
            ...state,
            isLoading: false,
            success: true,
          }));
        }
      });
  });

  return (
    <MainSection>
      <S.Inner>
        {formState.success ? (
          <FormSuccess bookLocation />
        ) : (
          <S.FormWrapper>
            <S.H4>Boka lokal</S.H4>
            <S.Wrapper>
              <S.H6>Bokningen görs för</S.H6>
              <S.FlexWrapper>
                <S.Button
                  className={active === "Privatperson" ? "active" : ""}
                  onClick={() => {
                    setActive("Privatperson");
                    updateFormProperty("bokningFor", "Privatperson");
                  }}
                >
                  Privatperson
                </S.Button>
                <S.Button
                  className={active === "Organisation" ? "active" : ""}
                  onClick={() => {
                    setActive("Organisation");
                    updateFormProperty("bokningFor", "Organisation");
                  }}
                >
                  Organisation/ grupp/ förening
                </S.Button>
              </S.FlexWrapper>
            </S.Wrapper>
            <S.Wrapper>
              <S.DetailsLarge>Obligatoriska fält</S.DetailsLarge>
            </S.Wrapper>
            <S.Form onSubmit={submitForm}>
              {active === "Privatperson" ? (
                <>
                  <S.Wrapper>
                    <S.Label htmlFor="namn">Ditt namn</S.Label>
                    <S.Input
                      type="text"
                      {...register("namn", { required: true })}
                      className={errors.namn ? "error" : ""}
                      placeholder="Ditt namn"
                      value={formState.data.namn}
                      onChange={(e) =>
                        updateFormProperty("namn", e.target.value)
                      }
                    />
                    {errors.namn && (
                      <S.Error role="alert">Namn är obligatoriskt</S.Error>
                    )}
                  </S.Wrapper>
                  <S.Wrapper>
                    <S.Label htmlFor="personNummer">Personnummer</S.Label>
                    <S.Input
                      type="text"
                      {...register("personNummer", { required: true })}
                      className={errors.personNummer ? "error" : ""}
                      placeholder="Personnummer"
                      onChange={(e) =>
                        updateFormProperty("personNummer", e.target.value)
                      }
                    />
                    {errors.personNummer && (
                      <S.Error role="alert">
                        Personnummer är obligatoriskt
                      </S.Error>
                    )}
                  </S.Wrapper>
                  <S.Wrapper>
                    <S.Label htmlFor="email">E-post</S.Label>
                    <S.Input
                      type="text"
                      {...register("email", { required: true })}
                      className={errors.email ? "error" : ""}
                      placeholder="E-post"
                      onChange={(e) =>
                        updateFormProperty("email", e.target.value)
                      }
                    />
                    {errors.email && (
                      <S.Error role="alert">E-post är obligatoriskt</S.Error>
                    )}
                  </S.Wrapper>
                </>
              ) : (
                <>
                  <S.Wrapper>
                    <S.Label>Är ni</S.Label>
                    <S.FlexWrapper>
                      <div>
                        <S.RadioButton
                          type="radio"
                          {...register("organisationTyp", { required: true })}
                          value="Sektion"
                          className={
                            errors.organisationTyp
                              ? "error custom-radio"
                              : "custom-radio"
                          }
                          onChange={(e) =>
                            updateFormProperty(
                              "organisationTyp",
                              e.target.value
                            )
                          }
                        />
                        <S.SmallLabel htmlFor="organisationTyp">
                          Sektion
                        </S.SmallLabel>
                      </div>
                      <div>
                        <S.RadioButton
                          type="radio"
                          {...register("organisationTyp", { required: true })}
                          className={
                            errors.organisationTyp
                              ? "error custom-radio"
                              : "custom-radio"
                          }
                          value="Organisation/Förening"
                          onChange={(e) =>
                            updateFormProperty(
                              "organisationTyp",
                              e.target.value
                            )
                          }
                        />
                        <S.SmallLabel htmlFor="organisationTyp">
                          Organisation/Förening
                        </S.SmallLabel>
                      </div>
                      <div>
                        <S.RadioButton
                          type="radio"
                          {...register("organisationTyp", { required: true })}
                          value="Företag"
                          className={
                            errors.organisationTyp
                              ? "error custom-radio"
                              : "custom-radio"
                          }
                          onChange={(e) =>
                            updateFormProperty(
                              "organisationTyp",
                              e.target.value
                            )
                          }
                        />
                        <S.SmallLabel htmlFor="organisationTyp">
                          Företag
                        </S.SmallLabel>
                      </div>
                    </S.FlexWrapper>
                    {errors.organisationTyp && (
                      <S.Error role="alert">
                        Välj vilken typ av organisation ni är
                      </S.Error>
                    )}
                  </S.Wrapper>
                  <S.Wrapper>
                    <S.Label htmlFor="namn">
                      Namn på sektion/organisation/företag
                    </S.Label>
                    <S.Input
                      type="text"
                      {...register("namn", { required: true })}
                      className={errors.namn ? "error" : ""}
                      placeholder="Namn"
                      onChange={(e) =>
                        updateFormProperty("namn", e.target.value)
                      }
                    />
                    {errors.namn && (
                      <S.Error role="alert">Namn är obligatoriskt</S.Error>
                    )}
                  </S.Wrapper>
                </>
              )}

              <S.Wrapper>
                <S.Label htmlFor="adress">Adress</S.Label>
                <S.Input
                  type="text"
                  {...register("adress", { required: true })}
                  className={errors.adress ? "error" : ""}
                  placeholder="Adress"
                  onChange={(e) => updateFormProperty("adress", e.target.value)}
                />
                {errors.adress && (
                  <S.Error role="alert">Adress är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.FlexWrapper>
                <S.Wrapper>
                  <S.Label htmlFor="postNummer">Postnummer</S.Label>
                  <S.Input
                    type="text"
                    {...register("postNummer", { required: true })}
                    className={errors.postNummer ? "error" : ""}
                    placeholder="Postnummer"
                    onChange={(e) =>
                      updateFormProperty("postNummer", e.target.value)
                    }
                  />
                  {errors.postNummer && (
                    <S.Error role="alert">Postnummer är obligatoriskt</S.Error>
                  )}
                </S.Wrapper>
                <S.Wrapper>
                  <S.Label htmlFor="stad">Postort</S.Label>
                  <S.Input
                    type="text"
                    {...register("stad", { required: true })}
                    className={errors.stad ? "error" : ""}
                    placeholder="Postort"
                    onChange={(e) => updateFormProperty("stad", e.target.value)}
                  />
                  {errors.stad && (
                    <S.Error role="alert">Postort är obligatoriskt</S.Error>
                  )}
                </S.Wrapper>
              </S.FlexWrapper>
              <S.Wrapper>
                <S.Label htmlFor="lokal">Vilken lokal vill du boka?</S.Label>
                <S.FlexWrapper>
                  <div>
                    <S.RadioButton
                      type="radio"
                      {...register("lokal", { required: true })}
                      className={
                        errors.lokal ? "error custom-radio" : "custom-radio"
                      }
                      value="SDF:s gård (utomhus)"
                      onChange={(e) =>
                        updateFormProperty("lokal", e.target.value)
                      }
                    />
                    <S.SmallLabel htmlFor="lokal">
                      SDF:s gård (utomhus)
                    </S.SmallLabel>
                  </div>
                  <div>
                    <S.RadioButton
                      type="radio"
                      {...register("lokal", { required: true })}
                      className={
                        errors.lokal ? "error custom-radio" : "custom-radio"
                      }
                      value="SDF:s gård (hela)"
                      onChange={(e) =>
                        updateFormProperty("lokal", e.target.value)
                      }
                    />
                    <S.SmallLabel htmlFor="lokal">
                      SDF:s gård (hela)
                    </S.SmallLabel>
                  </div>
                </S.FlexWrapper>
                <S.FlexWrapper>
                  <div>
                    {" "}
                    <S.RadioButton
                      type="radio"
                      {...register("lokal", { required: true })}
                      className={
                        errors.lokal ? "error custom-radio" : "custom-radio"
                      }
                      value="Dövas Hus (hela)"
                      onChange={(e) =>
                        updateFormProperty("lokal", e.target.value)
                      }
                    />
                    <S.SmallLabel htmlFor="lokal">
                      Dövas Hus (hela)
                    </S.SmallLabel>
                  </div>
                  <div>
                    <S.RadioButton
                      type="radio"
                      {...register("lokal", { required: true })}
                      className={
                        errors.lokal ? "error custom-radio" : "custom-radio"
                      }
                      value="Dövas Hus (hela)"
                      onChange={(e) =>
                        updateFormProperty("lokal", e.target.value)
                      }
                    />
                    <S.SmallLabel htmlFor="lokal">
                      Dövas Hus (hela)
                    </S.SmallLabel>
                  </div>
                </S.FlexWrapper>
                {errors.lokal && (
                  <S.Error role="alert">Välj en lokal att boka</S.Error>
                )}
              </S.Wrapper>
              {active === "Organisation" ? (
                <>
                  <S.Wrapper>
                    <S.Label htmlFor="valAvSyfte">
                      Vad används lokalen till?
                    </S.Label>
                    <S.FlexWrapper>
                      <div>
                        <S.RadioButton
                          type="radio"
                          {...register("valAvSyfte", { required: true })}
                          value="Konferens & möte"
                          className={
                            errors.valAvSyfte
                              ? "error custom-radio"
                              : "custom-radio"
                          }
                          onChange={(e) =>
                            updateFormProperty("valAvSyfte", e.target.value)
                          }
                        />
                        <S.SmallLabel htmlFor="valAvSyfte">
                          Konferens & möte
                        </S.SmallLabel>
                      </div>
                      <div>
                        <S.RadioButton
                          type="radio"
                          {...register("valAvSyfte", { required: true })}
                          className={
                            errors.valAvSyfte
                              ? "error custom-radio"
                              : "custom-radio"
                          }
                          value="Kurs"
                          onChange={(e) =>
                            updateFormProperty("valAvSyfte", e.target.value)
                          }
                        />
                        <S.SmallLabel htmlFor="valAvSyfte">Kurs</S.SmallLabel>
                      </div>
                      <div>
                        <S.RadioButton
                          type="radio"
                          {...register("valAvSyfte", { required: true })}
                          value="Fest & Event"
                          className={
                            errors.valAvSyfte
                              ? "error custom-radio"
                              : "custom-radio"
                          }
                          onChange={(e) =>
                            updateFormProperty("valAvSyfte", e.target.value)
                          }
                        />
                        <S.SmallLabel htmlFor="valAvSyfte">
                          Fest & Event
                        </S.SmallLabel>
                      </div>
                      <div>
                        <S.RadioButton
                          type="radio"
                          {...register("valAvSyfte", { required: true })}
                          value="Annat"
                          className={
                            errors.valAvSyfte
                              ? "error custom-radio"
                              : "custom-radio"
                          }
                          onChange={(e) =>
                            updateFormProperty("valAvSyfte", e.target.value)
                          }
                        />
                        <S.SmallLabel htmlFor="valAvSyfte">Annat</S.SmallLabel>
                      </div>
                    </S.FlexWrapper>
                    {errors.valAvSyfte && (
                      <S.Error role="alert">
                        Välj vad lokalen ska användas till
                      </S.Error>
                    )}
                  </S.Wrapper>
                </>
              ) : (
                <>
                  <S.Wrapper>
                    <S.Label htmlFor="valAvSyfte">
                      Vad används lokalen till?
                    </S.Label>
                    <S.FlexWrapper>
                      <div>
                        <S.RadioButton
                          type="radio"
                          {...register("valAvSyfte", { required: true })}
                          className={
                            errors.valAvSyfte
                              ? "error custom-radio"
                              : "custom-radio"
                          }
                          value="Privat sammankomst"
                          onChange={(e) =>
                            updateFormProperty("valAvSyfte", e.target.value)
                          }
                        />
                        <S.SmallLabel htmlFor="valAvSyfte">
                          Privat sammankomst
                        </S.SmallLabel>
                      </div>
                      <div>
                        <S.RadioButton
                          type="radio"
                          {...register("valAvSyfte", { required: true })}
                          className={
                            errors.valAvSyfte
                              ? "error custom-radio"
                              : "custom-radio"
                          }
                          value="Annat"
                          onChange={(e) =>
                            updateFormProperty("valAvSyfte", e.target.value)
                          }
                        />
                        <S.SmallLabel htmlFor="valAvSyfte">Annat</S.SmallLabel>
                      </div>
                    </S.FlexWrapper>
                    {errors.valAvSyfte && (
                      <S.Error role="alert">
                        Välj vad lokalen ska användas till
                      </S.Error>
                    )}
                  </S.Wrapper>
                </>
              )}

              <S.FlexWrapper>
                <S.Wrapper>
                  <S.Label htmlFor="startDatum">Startdatum</S.Label>
                  <Controller
                    control={control}
                    {...register("startDatum", { required: true })}
                    rules={{ required: "Date selection is required" }}
                    render={({ field }) => (
                      <DatePicker
                        onChange={(date: Date) => {
                          field.onChange(date);
                          updateFormProperty(
                            "startDatum",
                            date.toLocaleDateString("sv-SE")
                          );
                        }}
                        selected={field.value}
                        locale={sv}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="åååå-mm-dd"
                        calendarClassName="date-picker-calendar"
                        className={
                          errors.startDatum
                            ? "error date-picker"
                            : "date-picker"
                        }
                        showIcon
                        icon={
                          <svg
                            width="18"
                            height="19"
                            viewBox="0 0 18 19"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g clipPath="url(#clip0_418_37988)">
                              <path
                                d="M14.25 3.77002H3.75C2.92157 3.77002 2.25 4.44159 2.25 5.27002V15.77C2.25 16.5984 2.92157 17.27 3.75 17.27H14.25C15.0784 17.27 15.75 16.5984 15.75 15.77V5.27002C15.75 4.44159 15.0784 3.77002 14.25 3.77002Z"
                                stroke="#333333"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M12 2.27002V5.27002"
                                stroke="#333333"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M6 2.27002V5.27002"
                                stroke="#333333"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M2.25 8.27002H15.75"
                                stroke="#333333"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_418_37988">
                                <rect
                                  width="18"
                                  height="18"
                                  fill="white"
                                  transform="translate(0 0.77002)"
                                />
                              </clipPath>
                            </defs>
                          </svg>
                        }
                      />
                    )}
                  />
                  {errors.startDatum && (
                    <S.Error role="alert">Startdatum är obligatoriskt</S.Error>
                  )}
                </S.Wrapper>
                <S.Wrapper>
                  <S.Label htmlFor="slutDatum">Slutdatum</S.Label>
                  <Controller
                    control={control}
                    {...register("slutDatum", { required: true })}
                    rules={{ required: "Date selection is required" }}
                    render={({ field }) => (
                      <DatePicker
                        selected={field.value}
                        onChange={(date: Date | null) => {
                          field.onChange(date);
                          updateFormProperty(
                            "slutDatum",
                            date!.toLocaleDateString("sv-SE")
                          );
                        }}
                        locale={sv}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="åååå-mm-dd"
                        calendarClassName="date-picker-calendar"
                        className={
                          errors.slutDatum ? "error date-picker" : "date-picker"
                        }
                        showIcon
                        icon={
                          <svg
                            width="18"
                            height="19"
                            viewBox="0 0 18 19"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g clipPath="url(#clip0_418_37988)">
                              <path
                                d="M14.25 3.77002H3.75C2.92157 3.77002 2.25 4.44159 2.25 5.27002V15.77C2.25 16.5984 2.92157 17.27 3.75 17.27H14.25C15.0784 17.27 15.75 16.5984 15.75 15.77V5.27002C15.75 4.44159 15.0784 3.77002 14.25 3.77002Z"
                                stroke="#333333"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M12 2.27002V5.27002"
                                stroke="#333333"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M6 2.27002V5.27002"
                                stroke="#333333"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M2.25 8.27002H15.75"
                                stroke="#333333"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_418_37988">
                                <rect
                                  width="18"
                                  height="18"
                                  fill="white"
                                  transform="translate(0 0.77002)"
                                />
                              </clipPath>
                            </defs>
                          </svg>
                        }
                      />
                    )}
                  />
                  {errors.slutDatum && (
                    <S.Error role="alert">Slutdatum är obligatoriskt</S.Error>
                  )}
                </S.Wrapper>
              </S.FlexWrapper>

              <S.Wrapper>
                <S.Label htmlFor="syfteBeskrivning">Beskriv syftet</S.Label>
                <S.TextArea
                  {...register("syfteBeskrivning", { required: true })}
                  className={errors.syfteBeskrivning ? "error" : ""}
                  placeholder="Beskriv syftet"
                  onChange={(e) =>
                    updateFormProperty("syfteBeskrivning", e.target.value)
                  }
                />
                {errors.syfteBeskrivning && (
                  <S.Error role="alert">Detta fält är obligatoriskt</S.Error>
                )}
              </S.Wrapper>

              <S.Wrapper>
                <S.Label htmlFor="ovrigt" className="not-required">
                  Övriga upplysningar
                </S.Label>
                <S.SmallLabel>Här kan du ange övriga upplysningar</S.SmallLabel>
                <S.TextArea
                  name="ovrigt"
                  placeholder="Övriga upplysningar"
                  onChange={(e) => updateFormProperty("ovrigt", e.target.value)}
                  className="textarea-small"
                />
              </S.Wrapper>

              {active === "Privatperson" && (
                <S.Wrapper>
                  <S.Label>Är du medlem?</S.Label>
                  <S.FlexWrapper>
                    <div>
                      <S.RadioButton
                        type="radio"
                        {...register("medlem", { required: true })}
                        className={
                          errors.medlem ? "error custom-radio" : "custom-radio"
                        }
                        value="Ja"
                        onChange={(e) =>
                          updateFormProperty("medlem", e.target.value)
                        }
                      />
                      <S.SmallLabel htmlFor="medlem">Ja</S.SmallLabel>
                    </div>
                    <div>
                      <S.RadioButton
                        type="radio"
                        {...register("medlem", { required: true })}
                        value="Ej medlem"
                        className={
                          errors.medlem ? "error custom-radio" : "custom-radio"
                        }
                        onChange={(e) =>
                          updateFormProperty("medlem", e.target.value)
                        }
                      />
                      <S.SmallLabel htmlFor="medlem">Nej</S.SmallLabel>
                    </div>
                  </S.FlexWrapper>
                  {errors.medlem && (
                    <S.Error role="alert">Välj ett alternativ</S.Error>
                  )}
                </S.Wrapper>
              )}

              <S.Wrapper>
                <S.CheckboxContainer>
                  <S.CheckboxWrapper>
                    <S.Checkbox
                      type="checkbox"
                      {...register("accept", { required: true })}
                      className={errors.accept ? "error" : ""}
                    />
                    <svg
                      width="15"
                      height="13"
                      viewBox="0 0 15 13"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.1667 2.52002L5.37504 10.3117L1.83337 6.77002"
                        stroke="#281E78"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </S.CheckboxWrapper>
                  <S.SmallLabel htmlFor="accept">
                    Jag godkänner Stockholm Dövas Förenings{" "}
                    <a href="/">integritetspolicy.</a>
                  </S.SmallLabel>
                </S.CheckboxContainer>
              </S.Wrapper>
              <S.Wrapper>
                <S.SubmitButton type="submit">Skicka</S.SubmitButton>
                {formState.error && (
                  <S.FormError>
                    <S.Error>Något gick fel, vänligen försök igen.</S.Error>
                  </S.FormError>
                )}
              </S.Wrapper>
            </S.Form>
          </S.FormWrapper>
        )}
      </S.Inner>
    </MainSection>
  );
};

export default FormBookLocation;
